// btw-front/src/pages/PartnerDealSearch.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { EmptyState } from "../components/ui/EmptyState";
import { search as searchDeals } from "../api/partnerDeals";

export default function PartnerDealSearch() {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const reqIdRef = useRef(0);

  // 300ms 디바운스 — 빈 입력은 검색 X
  useEffect(() => {
    const q = keyword.trim();
    if (!q) {
      setItems([]);
      setLoading(false);
      return;
    }
    setTouched(true);
    const myReq = ++reqIdRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      searchDeals({ q })
        .then((rows) => {
          if (myReq !== reqIdRef.current) return;
          setItems(rows);
        })
        .catch(() => {
          if (myReq !== reqIdRef.current) return;
          setItems([]);
        })
        .finally(() => {
          if (myReq !== reqIdRef.current) return;
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  const showInitial = !touched && !keyword.trim();

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("partners.dealSearch.title")}
              </h1>
              <p className="mt-1 text-sm text-slate-600">{t("partners.dealSearch.subtitle")}</p>
            </div>
            <Link to="/partners/dashboard" className="btw-btn-ghost">
              {t("partners.dealSearch.backToDashboard")}
            </Link>
          </header>

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-3">
            <Input
              name="dealSearch"
              placeholder={t("partners.dealSearch.searchPlaceholder")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </section>

          <section className="mt-4">
            {showInitial ? (
              <EmptyState
                title={t("partners.dealSearch.initialTitle")}
                desc={t("partners.dealSearch.initialDesc")}
              />
            ) : loading ? (
              <p className="py-10 text-center text-sm text-slate-400">
                {t("partners.common.loading")}
              </p>
            ) : items.length === 0 ? (
              <EmptyState
                title={t("partners.dealSearch.emptyTitle")}
                desc={t("partners.dealSearch.emptyDesc")}
              />
            ) : (
              <>
                {/* 데스크탑 테이블 — 행 클릭 비활성 (상세 진입 차단) */}
                <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dealSearch.table.projectName")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dealSearch.table.customer")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dealSearch.table.partner")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.dealSearch.table.status")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((d) => (
                        <tr key={d.id} className="cursor-default">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {d.isMine && (
                                <span
                                  aria-hidden="true"
                                  className="inline-block h-4 w-0.5 rounded-full bg-accent"
                                />
                              )}
                              <span className="font-medium text-slate-900">{d.projectName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{d.customerCompany}</td>
                          <td className="px-4 py-3 text-slate-700">
                            {d.partnerCompanyName}
                            {d.isMine && (
                              <span className="ml-1.5 text-xs text-accent">
                                {t("partners.dealSearch.mineTag")}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일 카드 */}
                <div className="grid gap-2 md:hidden">
                  {items.map((d) => (
                    <div
                      key={d.id}
                      className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-900">{d.projectName}</span>
                        <Badge tone={d.status}>{t(`partners.deals.status.${d.status}`)}</Badge>
                      </div>
                      <div className="text-xs text-slate-600">{d.customerCompany}</div>
                      <div className="text-xs text-slate-500">
                        {d.partnerCompanyName}
                        {d.isMine && (
                          <span className="ml-1.5 text-accent">
                            {t("partners.dealSearch.mineTag")}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <p className="mt-3 text-xs text-slate-400">
                  {t("partners.dealSearch.privacyNote")}
                </p>
              </>
            )}
          </section>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
